import { useEffect, useLayoutEffect, useState } from 'react';
import useBrowserCompat from '@/hooks/useBrowserCompat';

type ReturnType = [boolean, (locked: boolean) => void];
const BODY_LOCK_KEY = '__dtsBodyLocks';
const ORIG_OVERFLOW_KEY = '__dtsOrigOverflow';
const ORIG_PADDING_RIGHT_KEY = '__dtsOrigPaddingRight';
const ORIG_TOUCH_ACTION_KEY = '__dtsOrigTouchAction';
const ORIG_SCROLL_POS_KEY = '__dtsOrigScrollPos';

const addLock = () => {
  const nLocks = document.body[BODY_LOCK_KEY] ?? 0;

  document.body[BODY_LOCK_KEY] = nLocks + 1;
  return nLocks === 0;
};

const removeLock = () => {
  const nLocks = document.body[BODY_LOCK_KEY] ?? 0;
  document.body[BODY_LOCK_KEY] = nLocks - 1;
  return nLocks === 1;
};

function useLockedBody(
  initialLocked = false,
  onLockChanged: ((locked: boolean) => void) | undefined = undefined
): ReturnType {
  const [locked, setLocked] = useState(initialLocked);
  const browserCompat = useBrowserCompat();

  // Do the side effect before render
  useLayoutEffect(() => {
    if (!locked) {
      onLockChanged?.(false);
      return;
    }

    if (addLock()) {
      // Save initial body style
      document.body[ORIG_OVERFLOW_KEY] = document.body.style.overflow;
      document.body[ORIG_PADDING_RIGHT_KEY] = document.body.style.paddingRight;
      document.body[ORIG_TOUCH_ACTION_KEY] = document.body.style.touchAction;
      document.body[ORIG_SCROLL_POS_KEY] = window.scrollY;

      // Lock body scroll
      document.body.style.overflow = 'hidden';

      // Get the scrollBar width
      const root = document.getElementById('root'); // or root
      const scrollBarWidth = root ? root.offsetWidth - root.scrollWidth : 0;

      // Avoid width reflow
      if (scrollBarWidth) {
        document.body.style.paddingRight = `${scrollBarWidth}px`;
      }

      // Disable panning for Safari, because the modal doesn't block touch events from reaching the body
      if (browserCompat?.isSafari) {
        document.body.style.touchAction = 'pinch-zoom';
      }
      document.body.style.position = 'fixed';
      document.body.style.top = `${-document.body[ORIG_SCROLL_POS_KEY]}px`;
    }

    onLockChanged?.(true);

    return () => {
      onLockChanged?.(false);

      if (removeLock()) {
        document.body.style.overflow = document.body[ORIG_OVERFLOW_KEY];
        document.body.style.touchAction = document.body[ORIG_TOUCH_ACTION_KEY];
        document.body.style.paddingRight = document.body[ORIG_PADDING_RIGHT_KEY];
        document.body.style.position = '';
        document.body.style.top = '';
        window.scrollTo(0, document.body[ORIG_SCROLL_POS_KEY]);
      }
    };
  }, [locked]);

  // Update state if initialValue changes
  useEffect(() => {
    if (locked !== initialLocked) {
      setLocked(initialLocked);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLocked]);

  return [locked, setLocked];
}

export default useLockedBody;
